import {
  opportunityBlockedFields,
  opportunityFieldLabel,
  opportunityValueLabel,
} from "./opportunityPresentation";
/**
 * Pure parsing, filtering and sorting for the R5-B Opportunity Analyst projection.
 *
 * The TypeScript types are erased at runtime, so an older backend, a proxy or a
 * tampered response must be rejected here before any row/summary is rendered. This
 * module never calls the network and never turns an untrusted value into a link.
 */

import type {
  OpportunityRecord,
  OpportunityResult,
  OpportunityResultAvailable,
  OpportunitySummary,
} from "../api/client";

export type OpportunityStatus = "all" | "draft_candidate" | "needs_review" | "duplicate" | "rejected";
export type OpportunitySort = "original" | "name" | "fit" | "confidence" | "status";

export interface OpportunityFilters {
  query: string;
  status: OpportunityStatus;
  type: string;
  region: string;
  reason: string;
}

export const EMPTY_OPPORTUNITY_FILTERS: OpportunityFilters = {
  query: "",
  status: "all",
  type: "",
  region: "",
  reason: "",
};

const PROJECTION_SCHEMA_VERSION = "r5.opportunity_result.v1";
const OUTCOMES = new Set(["draft_candidate", "needs_review", "duplicate", "rejected"]);
const MAX_RESULT_BYTES = 256 * 1024;
const DIGEST = /^sha256:[0-9a-f]{64}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const SAFE_UNAVAILABLE_REASONS = new Set([
  "job_not_finished",
  "unsupported_workflow",
  "workflow_mismatch",
  "step_linkage_mismatch",
  "integrity_rejected",
  "projection_too_large",
  "projection_unavailable",
]);

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOwn(value: UnknownRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function hasAllowedKeys(value: UnknownRecord, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key));
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) => hasOwn(value, key));
}

function isBoundedText(value: unknown, maximum: number): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= maximum;
}

function isBoundedTextOrNull(value: unknown, maximum: number): value is string | null {
  return value === null || isBoundedText(value, maximum);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isTextArray(value: unknown, maximum: number, itemMaximum: number): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= maximum &&
    value.every((entry) => isBoundedText(entry, itemMaximum))
  );
}

function isScore(value: unknown): value is number | null {
  return value === null || (isNonNegativeInteger(value) && value <= 100);
}

function isDigest(value: unknown): value is string {
  return typeof value === "string" && DIGEST.test(value);
}

function isDateText(value: unknown): value is string {
  if (!isBoundedText(value, 10) || !ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function isSafeHttpsUrl(value: unknown): value is string {
  if (!isBoundedText(value, 2048)) return false;
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      parsed.hostname.length > 0 &&
      !parsed.username &&
      !parsed.password &&
      !parsed.search &&
      !parsed.hash
    );
  } catch {
    return false;
  }
}

function isSource(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["source_id", "url", "observed_at", "claim", "claims"])
  ) {
    return false;
  }
  if (
    !isBoundedText(value.source_id, 64) ||
    !isSafeHttpsUrl(value.url) ||
    !isDateText(value.observed_at) ||
    !isBoundedText(value.claim, 1000)
  ) {
    return false;
  }
  return (
    Array.isArray(value.claims) &&
    value.claims.length <= 8 &&
    value.claims.every(
      (claim) =>
        isRecord(claim) &&
        hasExactKeys(claim, ["field", "value"]) &&
        isBoundedText(claim.field, 64) &&
        isBoundedText(claim.value, 512),
    )
  );
}

function isVerifiedFact(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["field", "value", "source_ids"]) &&
    isBoundedText(value.field, 64) &&
    isBoundedText(value.value, 512) &&
    isTextArray(value.source_ids, 4, 64)
  );
}

function isInferredFact(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["field", "value", "status", "basis"]) &&
    isBoundedText(value.field, 64) &&
    isBoundedText(value.value, 256) &&
    isBoundedText(value.status, 32) &&
    isBoundedText(value.basis, 256)
  );
}

function isContradiction(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["field", "values"]) &&
    isBoundedText(value.field, 64) &&
    isTextArray(value.values, 4, 512)
  );
}

function isFitComponent(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["component", "weight", "evidence"]) &&
    isBoundedText(value.component, 64) &&
    isNonNegativeInteger(value.weight) &&
    value.weight <= 100 &&
    isTextArray(value.evidence, 8, 128)
  );
}

function isSuppression(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["suppressed", "reasons"]) &&
    typeof value.suppressed === "boolean" &&
    isTextArray(value.reasons, 8, 256)
  );
}

const RECORD_KEYS = [
  "organization_id",
  "organization_name",
  "organization_type",
  "region",
  "outcome",
  "opportunity_type",
  "fit_score",
  "confidence",
  "fit_components",
  "reason_codes",
  "sources",
  "verified_facts",
  "inferred_facts",
  "missing_fields",
  "contradictions",
  "uncited_fields",
  "dedupe_key",
  "duplicate_of",
  "suppression",
  "recommended_next_action",
  "recommendation_draft_only",
  "machine_classification_only",
  "human_review_status",
  "human_review_required",
  "human_review_minutes",
  "contract_error_code",
] as const;

function isRecordRow(value: unknown): value is OpportunityRecord {
  if (!isRecord(value) || !hasExactKeys(value, RECORD_KEYS)) return false;
  if (!isBoundedText(value.organization_id, 64)) return false;
  if (!OUTCOMES.has(value.outcome as string)) return false;
  if (!isBoundedTextOrNull(value.organization_name, 200)) return false;
  if (!isBoundedTextOrNull(value.organization_type, 512)) return false;
  if (!isBoundedTextOrNull(value.region, 512)) return false;
  if (!isBoundedTextOrNull(value.opportunity_type, 64)) return false;
  if (!isScore(value.fit_score) || !isScore(value.confidence)) return false;
  if (value.outcome === "rejected" && (value.fit_score !== null || value.confidence !== null)) {
    return false;
  }
  if (!isTextArray(value.reason_codes, 30, 200) || value.reason_codes.length === 0) {
    return false;
  }
  if (!isTextArray(value.missing_fields, 8, 64)) return false;
  if (!isTextArray(value.uncited_fields, 8, 64)) return false;
  if (!isBoundedTextOrNull(value.dedupe_key, 256)) return false;
  if (!isBoundedTextOrNull(value.duplicate_of, 64)) return false;
  if (!isBoundedTextOrNull(value.contract_error_code, 200)) return false;
  if (!isBoundedText(value.recommended_next_action, 500)) return false;
  if (value.recommendation_draft_only !== true) return false;
  if (value.machine_classification_only !== true) return false;
  if (value.human_review_status !== "pending") return false;
  if (value.human_review_required !== true) return false;
  if (value.human_review_minutes !== null) return false;
  if (!Array.isArray(value.sources) || value.sources.length > 6) return false;
  if (!value.sources.every((source) => isSource(source))) return false;
  if (!Array.isArray(value.verified_facts) || value.verified_facts.length > 24) return false;
  if (!value.verified_facts.every((fact) => isVerifiedFact(fact))) return false;
  if (!Array.isArray(value.inferred_facts) || value.inferred_facts.length > 4) return false;
  if (!value.inferred_facts.every((fact) => isInferredFact(fact))) return false;
  if (!Array.isArray(value.contradictions) || value.contradictions.length > 8) return false;
  if (!value.contradictions.every((row) => isContradiction(row))) return false;
  if (!Array.isArray(value.fit_components) || value.fit_components.length > 8) return false;
  if (!value.fit_components.every((row) => isFitComponent(row))) return false;
  return isSuppression(value.suppression);
}

const SUMMARY_KEYS = [
  "produced",
  "draft_candidate",
  "needs_review",
  "duplicate",
  "rejected",
  "human_review_pending",
  "contract_rejected",
] as const;

function isSummary(value: unknown): value is OpportunitySummary {
  if (!isRecord(value) || !hasExactKeys(value, SUMMARY_KEYS)) return false;
  if (!SUMMARY_KEYS.every((field) => isNonNegativeInteger(value[field]) && value[field] <= 20)) {
    return false;
  }
  return value.produced === 20 && value.human_review_pending === 20;
}

function isCost(value: unknown): boolean {
  if (!isRecord(value) || typeof value.known !== "boolean") return false;
  if (value.known === false) return hasExactKeys(value, ["known"]);
  if (
    !hasExactKeys(value, [
      "known",
      "actual_micros",
      "estimated_micros",
      "unavailable_count",
      "currency",
    ])
  ) {
    return false;
  }
  return (
    isNonNegativeInteger(value.actual_micros) &&
    isNonNegativeInteger(value.estimated_micros) &&
    isNonNegativeInteger(value.unavailable_count) &&
    isBoundedText(value.currency, 8)
  );
}

function encodedByteSize(value: unknown): number | null {
  try {
    const serialized = JSON.stringify(value);
    if (typeof serialized !== "string") return null;
    if (typeof TextEncoder !== "undefined") {
      return new TextEncoder().encode(serialized).byteLength;
    }
    return serialized.length * 2;
  } catch {
    return null;
  }
}

const AVAILABLE_REQUIRED_KEYS = [
  "schema_version",
  "state",
  "workflow",
  "project_id",
  "evaluated_on",
  "fixture",
  "snapshot_hash",
  "analysis_output_hash",
  "review_output_hash",
  "decisions_hash",
  "summary",
  "records",
] as const;
const AVAILABLE_KEYS = [...AVAILABLE_REQUIRED_KEYS, "cost"];

/** A distinct R5-B version is required before any opportunity row is rendered. */
export function parseOpportunityResult(value: unknown): OpportunityResult | null {
  if (!isRecord(value) || value.schema_version !== PROJECTION_SCHEMA_VERSION) {
    return null;
  }
  if (value.state === "unavailable") {
    if (!hasExactKeys(value, ["schema_version", "state", "reason"])) return null;
    if (
      !isBoundedText(value.reason, 64) ||
      !SAFE_UNAVAILABLE_REASONS.has(value.reason)
    ) {
      return null;
    }
    const size = encodedByteSize(value);
    return size !== null && size <= MAX_RESULT_BYTES
      ? (value as unknown as OpportunityResult)
      : null;
  }
  if (value.state !== "available") return null;
  if (
    !hasAllowedKeys(value, AVAILABLE_KEYS) ||
    !AVAILABLE_REQUIRED_KEYS.every((key) => hasOwn(value, key))
  ) {
    return null;
  }
  if (
    !isRecord(value.workflow) ||
    !hasExactKeys(value.workflow, ["key", "version", "content_hash"]) ||
    value.workflow.key !== "megabooker.opportunity_analysis" ||
    value.workflow.version !== 1 ||
    !isDigest(value.workflow.content_hash)
  ) {
    return null;
  }
  if (!isBoundedText(value.project_id, 128) || !isDateText(value.evaluated_on)) {
    return null;
  }
  if (
    !isDigest(value.snapshot_hash) ||
    !isDigest(value.analysis_output_hash) ||
    !isDigest(value.review_output_hash) ||
    !isDigest(value.decisions_hash)
  ) {
    return null;
  }
  if (
    !isRecord(value.fixture) ||
    !hasExactKeys(value.fixture, ["id", "version", "hash"]) ||
    !isBoundedText(value.fixture.id, 64) ||
    !isBoundedText(value.fixture.version, 16) ||
    !isDigest(value.fixture.hash)
  ) {
    return null;
  }
  if (!isSummary(value.summary)) return null;
  if (!Array.isArray(value.records) || value.records.length !== 20) return null;
  if (!value.records.every((record) => isRecordRow(record))) return null;
  const organizationIds = new Set(value.records.map((record) => record.organization_id));
  if (organizationIds.size !== value.records.length) return null;
  if (hasOwn(value, "cost") && !isCost(value.cost)) return null;
  const size = encodedByteSize(value);
  if (size === null || size > MAX_RESULT_BYTES) return null;
  return value as unknown as OpportunityResult;
}


export function computedOpportunitySummary(
  result: OpportunityResultAvailable,
): OpportunitySummary {
  const counts = { draft_candidate: 0, needs_review: 0, duplicate: 0, rejected: 0 };
  let contractRejected = 0;
  for (const record of result.records) {
    counts[record.outcome] += 1;
    if (record.contract_error_code !== null) contractRejected += 1;
  }
  return {
    produced: result.records.length,
    draft_candidate: counts.draft_candidate,
    needs_review: counts.needs_review,
    duplicate: counts.duplicate,
    rejected: counts.rejected,
    human_review_pending: result.records.length,
    contract_rejected: contractRejected,
  };
}

/** Exact equality; the browser never repairs a mismatched count. */
export function opportunitySummaryMatches(result: OpportunityResultAvailable): boolean {
  const computed = computedOpportunitySummary(result);
  const actual = result.summary;
  return (
    actual.produced === computed.produced &&
    actual.draft_candidate === computed.draft_candidate &&
    actual.needs_review === computed.needs_review &&
    actual.duplicate === computed.duplicate &&
    actual.rejected === computed.rejected &&
    actual.human_review_pending === computed.human_review_pending &&
    actual.contract_rejected === computed.contract_rejected &&
    actual.produced ===
      actual.draft_candidate + actual.needs_review + actual.duplicate + actual.rejected
  );
}

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export function opportunityTypeLabel(type: string | null): string {
  if (!type) return "—";
  const labels: Record<string, string> = {
    agency_partnership: "Agencijsko partnerstvo",
    integration_foundation: "Integracijski temelj",
    portfolio_expansion: "Širenje portfelja",
    portfolio_channel_consolidation: "Konsolidacija kanala",
    direct_booking_growth: "Rast direktnih rezervacija",
    rate_and_payment_operations: "Cijene i plaćanja",
    baseline_operational_review: "Osnovni operativni pregled",
  };
  return labels[type] ?? type;
}

export function opportunityOutcomeLabel(outcome: string): string {
  switch (outcome) {
    case "draft_candidate":
      return "Prijedlog (strojno)";
    case "needs_review":
      return "Za ljudski pregled";
    case "duplicate":
      return "Duplikat";
    case "rejected":
      return "Odbijeno";
    default:
      return outcome;
  }
}

const REASON_LABELS: Record<string, string> = {
  evidence_complete: "Potrebni podaci potkrijepljeni su izvorima",
  uncited_claim: "Dio navoda nije potkrijepljen izvorima",
  contradictory_evidence: "Izvori navode različite podatke",
  missing_evidence: "Nedostaju podaci za pouzdanu procjenu",
  stale_evidence: "Dio izvora je zastario",
  suppressed_organization: "Organizacija je na listi isključenja",
  prompt_injection: "Izvor sadrži nedopuštene upute",
  duplicate_organization: "Organizacija je već evidentirana",
  duplicate_identity_ambiguous: "Identitet organizacije nije jednoznačan",
  contract_rejected: "Podaci nisu prošli provjeru ispravnosti",
  unsupported_field: "Zapis sadrži nepodržane podatke",
};
export function opportunityReasonLabel(code: string): string {
  if (REASON_LABELS[code]) return REASON_LABELS[code];
  if (code.startsWith("type_")) return `Vrsta organizacije: ${opportunityValueLabel(code.slice(5))}`;
  if (code.startsWith("band_")) return `Veličina portfelja: ${opportunityValueLabel(code.slice(5).replace(/_/g, "-").replace("plus", "+"))}`;
  if (code.startsWith("integration_")) return code.slice(12) === "none" ? "Nema zabilježenih integracija" : `U upotrebi: ${opportunityValueLabel(code.slice(12))}`;
  if (code.startsWith("signal_")) return opportunityValueLabel(code.slice(7));
  if (code.startsWith("category_")) return `Mogući smjer: ${opportunityTypeLabel(code.slice(9))}`;
  for (const [prefix, label] of [["missing_", "Nedostaje"], ["uncited_", "Izvor ne potvrđuje"], ["stale_", "Zastarjeli podaci"], ["contradiction_", "Izvori se ne slažu"]]) {
    if (code.startsWith(prefix)) return `${label}: ${opportunityFieldLabel(code.slice(prefix.length))}`;
  }
  if (code.startsWith("basis_")) return "Smjer analize izveden je iz potvrđenih podataka";
  return "Potrebna je dodatna provjera podataka";
}
const FACT_REASON_FIELDS: ReadonlyArray<[string, string]> = [
  ["signal_", "observed_sales_signals"],
  ["integration_", "integrations_in_use"],
];

/**
 * A signal/integration reason is only a readable assertion when the projection
 * contains the same value as a verified fact, the field is not blocked by
 * missing/uncited/stale/contradictory evidence, and the record was not rejected.
 * Otherwise the raw code stays available under technical disclosure.
 */
function factReasonSupported(record: OpportunityRecord, code: string, blocked: Set<string>): boolean {
  for (const [prefix, field] of FACT_REASON_FIELDS) {
    if (!code.startsWith(prefix)) continue;
    if (record.outcome === "rejected" || blocked.has(field)) return false;
    const value = code.slice(prefix.length);
    return record.verified_facts.some((fact) => fact.field === field && fact.value === value);
  }
  return true;
}

/** De-duplicate explanations; show evidence problems first. Raw codes remain in details. */
export function opportunityReasons(record: OpportunityRecord): string[] {
  const priority = (code: string) => /^(missing|uncited|stale|contradiction|suppressed|prompt|contract|duplicate|unsupported)/.test(code) ? 0 : 1;
  const blocked = opportunityBlockedFields(record);
  const codes = [...record.reason_codes].sort((a, b) => priority(a) - priority(b));
  return Array.from(new Set(codes
    .filter((code) => !/^(basis_|category_|type_|band_)/.test(code))
    .filter((code) => factReasonSupported(record, code, blocked))
    .map(opportunityReasonLabel)));
}

/** R5 evidence links: absolute HTTPS only, no credentials, query or fragment. */
export function safeOpportunityUrl(value: string | null | undefined): string | null {
  if (!value || value.length > 2048) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:") return null;
    if (!parsed.hostname) return null;
    if (parsed.username || parsed.password) return null;
    if (parsed.search || parsed.hash) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function filterOpportunities(
  records: OpportunityRecord[],
  filters: OpportunityFilters,
): OpportunityRecord[] {
  const query = normalize(filters.query.trim());
  return records.filter((record) => {
    const haystack = [
      record.organization_name ?? "",
      record.organization_id,
      record.organization_type ?? "",
      record.region ?? "",
      opportunityTypeLabel(record.opportunity_type),
      opportunityOutcomeLabel(record.outcome),
      ...record.reason_codes.map(opportunityReasonLabel),
    ].join(" ");
    return (
      (!query || normalize(haystack).includes(query)) &&
      (filters.status === "all" || record.outcome === filters.status) &&
      (!filters.type ||
        (filters.type === "__unknown__"
          ? record.opportunity_type === null
          : record.opportunity_type === filters.type)) &&
      (!filters.region || record.region === filters.region) &&
      (!filters.reason || record.reason_codes.includes(filters.reason))
    );
  });
}

export function sortOpportunities(
  records: OpportunityRecord[],
  sort: OpportunitySort,
): OpportunityRecord[] {
  if (sort === "original") return records;
  const sorted = [...records];
  if (sort === "name") {
    sorted.sort((left, right) =>
      (left.organization_name ?? left.organization_id).localeCompare(
        right.organization_name ?? right.organization_id,
        "hr",
      ),
    );
  } else if (sort === "status") {
    sorted.sort((left, right) => left.outcome.localeCompare(right.outcome));
  } else {
    // A missing score is unknown, never zero: always sort it last.
    const key = sort === "fit" ? "fit_score" : "confidence";
    sorted.sort((left, right) => {
      const leftScore = left[key];
      const rightScore = right[key];
      if (leftScore === null && rightScore === null) return 0;
      if (leftScore === null) return 1;
      if (rightScore === null) return -1;
      return rightScore - leftScore;
    });
  }
  return sorted;
}

export function browseOpportunities(
  records: OpportunityRecord[],
  filters: OpportunityFilters,
  sort: OpportunitySort,
): OpportunityRecord[] {
  return sortOpportunities(filterOpportunities(records, filters), sort);
}

export function opportunityFilterOptions(
  records: OpportunityRecord[],
  key: "type" | "region" | "reason",
): string[] {
  const values = new Set<string>();
  for (const record of records) {
    if (key === "type") {
      values.add(record.opportunity_type ?? "__unknown__");
    } else if (key === "region") {
      if (record.region) values.add(record.region);
    } else {
      record.reason_codes.forEach((code) => values.add(code));
    }
  }
  return Array.from(values).sort((left, right) => left.localeCompare(right, "hr"));
}

export function opportunityUnavailableReason(reason: string): string {
  switch (reason) {
    case "job_not_finished":
      return "Posao još nije završio; rezultat se ne prikazuje unaprijed.";
    case "unsupported_workflow":
      return "Ovaj posao nije R5 Opportunity Analyst workflow.";
    case "workflow_mismatch":
      return "Workflow posla ne odgovara pregledanom R5 workflowu.";
    case "step_linkage_mismatch":
      return "Koraci posla ne odgovaraju očekivanom R5 lancu.";
    case "project_mismatch":
      return "Projekcija pripada drugom projektu; prikaz je blokiran.";
    case "projection_too_large":
      return "Rezultat prelazi dopuštenu veličinu prikaza.";
    case "invalid_projection":
      return "Projekcija rezultata nema poznati oblik; prikaz je blokiran.";
    case "integrity_rejected":
    case "projection_unavailable":
      return "Rezultat nije moguće provjeriti kroz cijeli lanac dokaza; prikaz je blokiran.";
    default:
      return "Rezultat nije dostupan.";
  }
}

